import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ServiceConnector, ServiceType, PlatformType } from './ServiceConnector';
import './ServiceConnectorModal.css';

// Re-export types from ServiceConnector
export type { ServiceType, PlatformType } from './ServiceConnector';

interface ServiceConnectorModalProps {
  service: ServiceType;
  platform: PlatformType;
  isOpen: boolean;
  onClose: () => void;
  onConnect: (service: ServiceType) => Promise<void>;
}

export const ServiceConnectorModal: React.FC<ServiceConnectorModalProps> = ({
  service,
  platform,
  isOpen,
  onClose,
  onConnect
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      // Add haptic feedback on mobile
      if ((platform === 'ios' || platform === 'android') && 'vibrate' in navigator) {
        navigator.vibrate(50);
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, platform]);

  if (!isOpen) return null;

  const handleClose = () => {
    // Add haptic feedback on close
    if ((platform === 'ios' || platform === 'android') && 'vibrate' in navigator) {
      navigator.vibrate(25);
    }
    onClose();
  };

  return createPortal(
    <div className="service-connector-modal-overlay" onClick={handleClose}>
      <div 
        className="service-connector-modal"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal header with handle for mobile */}
        <div className="modal-header">
          <div className="modal-handle"></div>
          <button 
            className="modal-close-btn"
            onClick={handleClose}
            aria-label="Close modal"
          >
            ✕
          </button>
        </div>

        {/* Service connector content */}
        <div className="modal-content">
          <ServiceConnector
            service={service}
            platform={platform}
            onConnect={onConnect}
            onCancel={handleClose}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};
